import React, { useEffect, useMemo, useState } from 'react';
import { getProductCodeList, getTravelRegionCategoryList, saveProductCodeRules, getProductCodeRuleInfo } from '../services/apiService';

const ProductCodeManagement = () => {
  const [productRows, setProductRows] = useState([]);
  const [regionRows, setRegionRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [searchProduct, setSearchProduct] = useState('');
  const [searchRegion, setSearchRegion] = useState('');
  const [editing, setEditing] = useState(null); // { ds: 'product'|'region', code }
  const [alphabetInput, setAlphabetInput] = useState('');
  const [pendingChanges, setPendingChanges] = useState({ productCodes: {}, travelRegions: {} });
  const [ruleUse, setRuleUse] = useState(true);

  const normalizeAlphabet = (value) => {
    const v = String(value ?? '')
      .toUpperCase()
      .replace(/[^A-Z]/g, '');
    return v.slice(0, 1);
  };

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [p, r, rule] = await Promise.all([
        getProductCodeList(),
        getTravelRegionCategoryList(),
        getProductCodeRuleInfo(),
      ]);
      setProductRows(Array.isArray(p) ? p : []);
      setRegionRows(Array.isArray(r) ? r : []);
      if (typeof rule === 'boolean') setRuleUse(rule);
    } catch (e) {
      setError('데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const types = useMemo(() => {
    const set = new Set(productRows.map(r => r?.상품종류).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [productRows]);

  const filteredProducts = useMemo(() => {
    return productRows.filter(r => {
      const typeOk = filterType === 'all' ? true : r?.상품종류 === filterType;
      const text = (searchProduct || '').toLowerCase();
      const textOk = !text ? true : (
        String(r?.구분 || '').toLowerCase().includes(text) ||
        String(r?.구분코드 || '').toLowerCase().includes(text)
      );
      return typeOk && textOk;
    });
  }, [productRows, filterType, searchProduct]);

  const filteredRegions = useMemo(() => {
    const text = (searchRegion || '').toLowerCase();
    return regionRows.filter(r => (
      String(r?.구분 || '').toLowerCase().includes(text) ||
      String(r?.구분코드 || '').toLowerCase().includes(text)
    ));
  }, [regionRows, searchRegion]);

  const startEdit = (ds, row) => {
    setEditing({ ds, code: row?.구분코드 });
    setAlphabetInput(normalizeAlphabet(row?.알파벳 ?? ''));
  };

  const cancelEdit = () => {
    setEditing(null);
    setAlphabetInput('');
  };

  const applyEdit = () => {
    if (!editing) return;
    const code = String(editing.code);
    const val = normalizeAlphabet(alphabetInput || '');
    if (editing.ds === 'product') {
      setProductRows(prev => prev.map(r => r?.구분코드 === code ? { ...r, 알파벳: val } : r));
      setPendingChanges(prev => ({ ...prev, productCodes: { ...(prev.productCodes || {}), [code]: val } }));
    } else {
      setRegionRows(prev => prev.map(r => r?.구분코드 === code ? { ...r, 알파벳: val } : r));
      setPendingChanges(prev => ({ ...prev, travelRegions: { ...(prev.travelRegions || {}), [code]: val } }));
    }
    cancelEdit();
  };

  const randomizeAll = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const shuffle = (arr) => {
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };
    // 1) Product codes: unique per 상품종류
    const byType = productRows.reduce((acc, r) => {
      const key = r?.상품종류 || '';
      (acc[key] = acc[key] || []).push(r);
      return acc;
    }, {});
    let updatedProducts = [...productRows];
    let productChanges = { ...(pendingChanges.productCodes || {}) };
    for (const [type, list] of Object.entries(byType)) {
      if (list.length > letters.length) {
        window.alert(`${type} 그룹 항목이 26개를 초과하여 고유 알파벳을 배정할 수 없습니다.`);
        continue;
      }
      const pool = shuffle(letters).slice(0, list.length);
      list.forEach((row, idx) => {
        const newAlpha = pool[idx];
        const code = String(row?.구분코드);
        const i = updatedProducts.findIndex(x => x?.구분코드 === code);
        if (i >= 0) updatedProducts[i] = { ...updatedProducts[i], 알파벳: newAlpha };
        productChanges[code] = newAlpha;
      });
    }
    // 2) Travel regions: unique across the list
    if (regionRows.length > letters.length) {
      window.alert('여행지 대분류 항목이 26개를 초과하여 고유 알파벳을 배정할 수 없습니다.');
    } else {
      const pool = shuffle(letters).slice(0, regionRows.length);
      const updatedRegions = regionRows.map((r, idx) => ({ ...r, 알파벳: pool[idx] }));
      const regionChanges = {};
      updatedRegions.forEach(r => { regionChanges[String(r?.구분코드)] = String(r?.알파벳 || ''); });
      setRegionRows(updatedRegions);
      setPendingChanges(prev => ({ ...prev, productCodes: productChanges, travelRegions: regionChanges }));
    }
    setProductRows(updatedProducts);
  };

  const resetAllChanges = async () => {
    setPendingChanges({ productCodes: {}, travelRegions: {} });
    await load();
  };

  const saveAllBulk = async () => {
    try {
      const products = (productRows || []).map(r => ({ 구분코드: String(r?.구분코드 || ''), 알파벳: normalizeAlphabet((pendingChanges.productCodes?.[String(r?.구분코드)] ?? r?.알파벳) || '') }));
      const regions = (regionRows || []).map(r => ({ 구분코드: String(r?.구분코드 || ''), 알파벳: normalizeAlphabet((pendingChanges.travelRegions?.[String(r?.구분코드)] ?? r?.알파벳) || '') }));
      const resp = await saveProductCodeRules({ productList: products, regionList: regions, ruleUse });
      const isOk = resp && typeof resp === 'object' && Object.keys(resp).length === 0;
      if (isOk) {
        window.alert('저장되었습니다.');
        setPendingChanges({ productCodes: {}, travelRegions: {} });
        await load();
      } else {
        window.alert('저장 실패');
      }
    } catch (e) {
      window.alert('저장 실패');
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="rounded-2xl bg-white p-5 flex items-center justify-between gap-2">
        <h1 className="text-[17px] font-bold text-gray-900">상품코드/여행지 대분류 관리</h1>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" className="h-4 w-4" checked={!!ruleUse} onChange={(e) => setRuleUse(e.target.checked)} />
            상품코드룰사용
          </label>
          <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={randomizeAll}>랜덤 배정</button>
          <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700" onClick={saveAllBulk}>일괄 저장</button>
          <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={resetAllChanges}>전체 변경 취소</button>
        </div>
      </div>

      {loading && <div className="rounded-2xl bg-white py-16 text-center text-sm text-gray-500">로딩 중...</div>}
      {!loading && error && <div className="rounded-2xl bg-white py-16 text-center text-sm text-red-600">{error}</div>}

      {!loading && !error && (
        <div className="grid gap-4">
          {/* Product Codes Section */}
          <section className="rounded-2xl bg-white p-5 overflow-hidden">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-[17px] font-bold text-gray-900">상품코드</h2>
              <div className="flex gap-2 items-center">
                <select className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  {types.map(t => (
                    <option key={t} value={t}>{t === 'all' ? '상품종류(전체)' : t}</option>
                  ))}
                </select>
                <input className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none" placeholder="구분/코드 검색" value={searchProduct} onChange={(e) => setSearchProduct(e.target.value)} />
                <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={() => { setFilterType('all'); setSearchProduct(''); }}>초기화</button>
              </div>
            </div>
            <div className="overflow-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">상품종류</th>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">구분</th>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">구분코드</th>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">고정</th>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">알파벳</th>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg"></th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredProducts.map((r, idx) => {
                    const isEditing = editing && editing.ds === 'product' && editing.code === r?.구분코드;
                    return (
                      <tr key={`${r?.구분코드 || idx}`} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm whitespace-nowrap">{r?.상품종류 ?? ''}</td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap">{r?.구분 ?? ''}</td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap">{r?.구분코드 ?? ''}</td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap">{r?.고정 ?? ''}</td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap">
                          {!isEditing && (
                            <span className="inline-block min-w-[2rem]">{r?.알파벳 ?? ''}</span>
                          )}
                          {isEditing && (
                            <input
                              className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none w-24"
                              value={alphabetInput}
                              onChange={(e) => setAlphabetInput(normalizeAlphabet(e.target.value))}
                              maxLength={1}
                              onKeyDown={(e) => { if (e.key === 'Enter') applyEdit(); if (e.key === 'Escape') cancelEdit(); }}
                              autoFocus
                            />
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap text-right">
                          {!isEditing && (
                            <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={() => startEdit('product', r)}>편집</button>
                          )}
                          {isEditing && (
                            <div className="flex gap-2 justify-end">
                              <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={cancelEdit}>취소</button>
                              <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700" onClick={applyEdit}>완료</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredProducts.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-16 text-center text-sm text-gray-500">표시할 데이터가 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Travel Regions Section */}
          <section className="rounded-2xl bg-white p-5 overflow-hidden">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-[17px] font-bold text-gray-900">여행지 대분류</h2>
              <div className="flex gap-2 items-center">
                <input className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none" placeholder="구분/코드 검색" value={searchRegion} onChange={(e) => setSearchRegion(e.target.value)} />
                <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={() => setSearchRegion('')}>초기화</button>
              </div>
            </div>
            <div className="overflow-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">구분</th>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">구분코드</th>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">알파벳</th>
                    <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg"></th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredRegions.map((r, idx) => {
                    const isEditing = editing && editing.ds === 'region' && editing.code === r?.구분코드;
                    return (
                      <tr key={`${r?.구분코드 || idx}`} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm whitespace-nowrap">{r?.구분 ?? ''}</td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap">{r?.구분코드 ?? ''}</td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap">
                          {!isEditing && (
                            <span className="inline-block min-w-[2rem]">{r?.알파벳 ?? ''}</span>
                          )}
                          {isEditing && (
                            <input
                              className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none w-24"
                              value={alphabetInput}
                              onChange={(e) => setAlphabetInput(normalizeAlphabet(e.target.value))}
                              maxLength={1}
                              onKeyDown={(e) => { if (e.key === 'Enter') applyEdit(); if (e.key === 'Escape') cancelEdit(); }}
                              autoFocus
                            />
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap text-right">
                          {!isEditing && (
                            <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={() => startEdit('region', r)}>편집</button>
                          )}
                          {isEditing && (
                            <div className="flex gap-2 justify-end">
                              <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={cancelEdit}>취소</button>
                              <button type="button" className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700" onClick={applyEdit}>완료</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredRegions.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-16 text-center text-sm text-gray-500">표시할 데이터가 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default ProductCodeManagement;
